import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Navigate, useNavigate } from "react-router"

import { Card } from "components/Card/Card"
import { FormPage } from "components/Form/Form"
import { useBirthLocation } from "components/Form/birthLocationProvider"
import profileActions from "containers/Profile/services/actions"
import surveyActions from "containers/Survey/services/actions"
import {
  getCurrentPatient,
  getCurrentSelectedPatientId,
} from "containers/Survey/services/selector"
import { SURVEY_PAGE } from "core/constants"
import { birthdateValidator } from "lib/form"
import { capitalize, getMissingBirthTraits } from "lib/utils"
import { useLanguage } from "locales"
import { BirthPlace, Gender, Relative } from "types/entity"
import { InputFormProps, InputNames } from "types/props"

/*
 * Asked right after the patient choice when the selected patient (main
 * account or relative) lacks identity traits. Birth traits can be skipped,
 * the relative gender cannot.
 */
const CompletePatientInfo = (): JSX.Element => {
  const { languages } = useLanguage()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { cleanBirthLocationData, updateBirthdate } = useBirthLocation()
  const selectedPatientId = useSelector(getCurrentSelectedPatientId)
  const patient = useSelector(getCurrentPatient)

  const isRelative = selectedPatientId !== 0
  const firstname = capitalize(patient?.firstname || "")
  const traitLabel = (yours: string, relatives: string) =>
    isRelative ? relatives.replace("$RELATIVE_NAME$", firstname) : yours
  const missingTraits = getMissingBirthTraits(patient)
  const missingGender =
    isRelative &&
    ![Gender.MALE, Gender.FEMALE].includes(patient?.gender as Gender)

  useEffect(() => {
    cleanBirthLocationData()
    if (patient?.birthdate?.format) {
      updateBirthdate(patient.birthdate.format("DDMMYYYY"))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!patient || (!missingGender && !missingTraits.length)) {
    return <Navigate to={SURVEY_PAGE} replace />
  }

  const inputs: InputFormProps[] = []
  if (missingGender) {
    inputs.push({
      name: InputNames.GENDER,
      required: true,
      keepLabelFormat: true,
      label: `${capitalize(languages.inputThe("gender"))}${firstname ? ` de ${firstname}` : ""}`,
    })
  }
  if (missingTraits.includes(InputNames.FIRST_BIRTH_FIRSTNAME)) {
    inputs.push({
      name: InputNames.FIRST_BIRTH_FIRSTNAME,
      required: false,
      keepLabelFormat: true,
      hidePseudoName: true,
      label: traitLabel(
        languages.enterYourBirthFirstname,
        languages.enterRelativeBirthFirstname,
      ),
    })
  }
  if (missingTraits.includes(InputNames.BIRTH_LASTNAME)) {
    inputs.push({
      name: InputNames.BIRTH_LASTNAME,
      required: false,
      keepLabelFormat: true,
      hidePseudoName: true,
      label: traitLabel(
        languages.enterYourBirthLastname,
        languages.enterRelativeBirthLastname,
      ),
    })
  }
  if (missingTraits.includes(InputNames.BIRTH_LOCATION)) {
    inputs.push(
      { name: InputNames.INSEE_CODE, label: "", type: "hidden" },
      {
        name: InputNames.BIRTHDATE,
        label: "",
        required: true,
        rules: [birthdateValidator],
        type: "hidden",
      },
      {
        name: InputNames.BIRTH_COUNTRY,
        type: "hidden",
        default: { value: `${BirthPlace.FRANCE}`, label: "" },
      },
      {
        name: InputNames.BIRTH_LOCATION,
        required: false,
        readOnly: false,
        keepLabelFormat: true,
        label: traitLabel(
          languages.enterYourBirthLocation,
          languages.enterRelativeBirthLocation,
        ),
      },
    )
  }

  const backToSurvey = () => {
    cleanBirthLocationData()
    navigate(SURVEY_PAGE)
  }

  // Only the inputs listed in `accepted` are saved: on skip, the value still
  // being typed is dropped
  const save = (values: any, accepted: string[]) => {
    const payload: Partial<Relative> = {}
    if (accepted.includes(InputNames.GENDER)) payload.gender = values.gender
    if (accepted.includes(InputNames.FIRST_BIRTH_FIRSTNAME) && values.first_birth_firstname)
      payload.first_birth_firstname = values.first_birth_firstname
    if (accepted.includes(InputNames.BIRTH_LASTNAME) && values.birth_lastname)
      payload.birth_lastname = values.birth_lastname
    if (accepted.includes(InputNames.BIRTH_LOCATION) && values.insee_code)
      payload.birth_location = values.insee_code
    if (!Object.keys(payload).length) return
    dispatch(
      isRelative
        ? profileActions.changeRelativeRequest({
            id: selectedPatientId as number,
            ...payload,
          })
        : profileActions.changePatientRequest(payload),
    )
  }

  return (
    <Card>
      <FormPage
        loading={false}
        inputs={inputs}
        prospect={patient}
        onFinish={(values) => {
          save(values, inputs.map((input) => input.name))
          backToSurvey()
        }}
        acceptText={languages.next}
        skipText={languages.later}
        onSkip={(values, accepted) => {
          save(values, accepted)
          backToSurvey()
        }}
        onCancel={() => {
          dispatch(surveyActions.previous())
          backToSurvey()
        }}
      />
    </Card>
  )
}

export default CompletePatientInfo
